import {useState, useEffect, useRef, useReducer, useMemo} from 'react'
import Select from 'react-select'
import { Spinner } from 'react-bootstrap';

import {useIntl} from 'react-intl'
import { adminAPI } from '../../../api';

import BootstrapDateRangePicker from '../../../components/BootstrapDateRangePicker'

import colorLib from '@kurkle/color';


import { Chart as ChartJS, ChartDataset } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {FEEDBACK_CATEGORIES} from '../../../models'
import {toTitleCase} from '../../../utils'
import {UserType} from '../../../models'

interface Props {
}


export function transparentize(value, opacity) {
  var alpha = opacity === undefined ? 0.5 : 1 - opacity;
  return colorLib(value).alpha(alpha).rgbString();
}
const REACTION_FORMAT = {'like':'good', 'dislike':'bad', 'great':'great'}
const REACTION_CATEGORIES = ['great', 'good', 'bad']
const REACTION_COLORS = {
  'great': transparentize('#17C653', 0.4),
  'good': transparentize('#F6C000', 0.4),
  'bad': transparentize('#F8285A', 0.4)
}

const COLORS = [
  'rgba(54,162,235,0.4)',  // Blue
  'rgba(153,102,255,0.4)', // Purple
  'rgba(255,159,64,0.4)',  // Orange
  'rgba(199,199,199,0.4)', // Gray
  'rgba(255,205,210,0.4)', // Pinkish
  'rgba(66,133,244,0.4)',  // Bright Blue
  'rgba(63,81,181,0.4)',   // Indigo
  'rgba(0,188,212,0.4)',   // Cyan
  'rgba(3,169,244,0.4)',   // Light Blue
  'rgba(158,158,158,0.4)', // Medium Gray
  'rgba(96,125,139,0.4)',  // Blue Gray
  'rgba(123,31,162,0.4)',  // Deep Purple
  'rgba(98,0,238,0.4)',    // Vivid Purple
  'rgba(68,138,255,0.4)',  // Bright Blue
  'rgba(33,150,243,0.4)',  // Light Blue
  'rgba(41,121,255,0.4)',  // Blue
  'rgba(48,63,159,0.4)',   // Dark Blue
  'rgba(63,81,181,0.4)',   // Indigo
  'rgba(255,87,34,0.4)',   // Deep Orange
  'rgba(191,54,12,0.4)',   // Brownish Orange
  'rgba(121,85,72,0.4)',   // Brown
]

const getRandomColors = (values : string[]) => {
  let shuffled = values.slice().sort(() => 0.5 - Math.random());
  let colors = {}
  shuffled.forEach((value, index) => {
    colors[value] = COLORS[index]
  })
  return colors
}

const formatModelId = (modelId: string) => {
  if( modelId) {
    let modelId_ = modelId.split('/')[modelId.split('/').length - 1]
    return modelId_ != '' ? modelId_ : modelId.split('/')[modelId.split('/').length - 2]
  }else{
    return ''
  }
}

const formatReaction = (reaction: string) => {
  return REACTION_FORMAT[reaction] || reaction
}


const dataReducer = (state, action) => {
  let newFilterCriteria;
  switch (action.type) {
    case 'SET_DATA':
      return {
        ...state,
        data: action.payload.data
      };
    case 'FILTER_BY_ATTRIBUTE':
      const existingFilter = state.filterCriteria.find(f => f.attribute === action.payload.attribute);

      if (existingFilter) {
        newFilterCriteria = state.filterCriteria.map(f =>
          f.attribute === action.payload.attribute
            ? { ...f, values: Array.from(new Set([...f.values, action.payload.value])) }
            : f
        );
      } else {
        newFilterCriteria = [
          ...state.filterCriteria,
          { attribute: action.payload.attribute, values: [action.payload.value] },
        ];
      }

      return {
        ...state,
        filterCriteria: newFilterCriteria
      };
    case 'REMOVE_FILTER':
      newFilterCriteria = state.filterCriteria.map(f =>
        f.attribute === action.payload.attribute
          ? { ...f, values: action.payload.value ? f.values.filter(value => value !== action.payload.value) : [] }
          : f
      ).filter(f => f.values.length > 0);

      return {
        ...state,
        filterCriteria: newFilterCriteria
      };
    default:
      return state;
  }
};

function groupByAndCount(data: any[], attributes: string[]) {
  return data.reduce((acc, item) => {
    const key = attributes.map(attr => item[attr]).join('|');
    if (!acc[key]) {
      acc[key] = 0;
    }
    acc[key] += 1;
    return acc;
  }, {} as Record<string, number>);
}

type ChartType = 'bar';
type SortOrder = 'asc' | 'desc' | undefined;
interface DynamicChartProps {
  title: string;
  data: any[];
  uniqueAttribute?: string;
  attribute1: string;
  label1: string;
  attribute2?: string; // Optional second attribute for vertical bar chart
  label2?: string; // Optional second label for vertical bar chart
  attribute3?: string; // Optional third attribute for stacked chart
  label3?: string; // Optional third label for stacked chart
  dispatch: any;
  filterCriteria: any[];
  labelFormatter?: (label: string) => string;
  sortOrder?: SortOrder;
  xAxisLimit?: number;
  xAxisOrder?: string[];
  xAxisStackOrder?: string[];
  yAxisOrder?: string[];
  colors?: {[key: string]: string};
}
const DynamicChart: React.FC<DynamicChartProps> = ({
  title,
  data,
  uniqueAttribute,
  attribute1,
  label1,
  attribute2,
  attribute3,
  dispatch,
  filterCriteria,
  labelFormatter = label => label,
  sortOrder = undefined,
  xAxisLimit,
  xAxisOrder,
  xAxisStackOrder,
  yAxisOrder,
  colors
}) => {
  const filteredValues = filterCriteria.filter(f => attribute1 == f.attribute).flatMap(f => f.values);

  const preprocessData = useMemo(() => {
    let excludedData = data.filter(item => {
      return filterCriteria.every(f => {
        if (attribute1 == f.attribute) {
          return true;
        }
        const itemValue = item[f.attribute];

        if (f.attribute === 'feedback_category' && itemValue && itemValue.includes(',') ) {
          const categories = itemValue.replace(', ', ',').split(',').map(category => category.trim());
          return categories.some(category => f.values.includes(category));
        }else{
          return f.values.includes(itemValue);
        }
      });
    });

    if( uniqueAttribute ){
      excludedData = Array.from(
        new Map(
          excludedData
            .map(item => [item.prompt_id, item]) 
        ).values()
      )
    }

    let groupedData = groupByAndCount(excludedData, [attribute1, attribute2, attribute3].filter(attr => attr !== undefined) as string[]);
    if (sortOrder) {
      if (attribute3 !== undefined) {
        const totalSums: Record<string, number> = {};
        
        for (const key of Object.keys(groupedData)) {
          const [attr1Value] = key.split('|');
          if (!totalSums[attr1Value]) {
            totalSums[attr1Value] = 0;
          }
          totalSums[attr1Value] += groupedData[key] as number;
        }
  
        groupedData = Object.fromEntries(
          Object.entries(groupedData).sort(([aKey], [bKey]) => {
            const aAttr1Value = aKey.split('|')[0];
            const bAttr1Value = bKey.split('|')[0];
            
            if (sortOrder === 'asc') {
              return totalSums[aAttr1Value] - totalSums[bAttr1Value];
            } else {
              return totalSums[bAttr1Value] - totalSums[aAttr1Value];
            }
          })
        );
      } else {
        groupedData = Object.fromEntries(
          Object.entries(groupedData).sort(([aKey, aValue], [bKey, bValue]) => {
            const aVal = aValue as number;
            const bVal = bValue as number;
            
            if (sortOrder === 'asc') {
              return aVal - bVal;
            } else {
              return bVal - aVal;
            }
          })
        );
      }
    }
    return groupedData;
  }, [data, attribute1, attribute2, attribute3, filterCriteria]);

  const groupedData = useMemo(() => preprocessData, [preprocessData]);

  let labels = Array.from(new Set(Object.keys(groupedData).map(key => key.split('|')[0])));
  let datasetLabels = Array.from(new Set(Object.keys(groupedData).map(key => key.split('|')[1])));
  let stackLabels = attribute3
    ? Array.from(new Set(Object.keys(groupedData).map(key => key.split('|')[2])))
    : [];

  const getAdditionalLabels = (preprocessData: Record<string, any>, index: number, order: string[]) => {
    return Object.keys(preprocessData)
      .map(key => key.split('|')[index])
      .filter(label => !order.includes(label));
  };

  if (xAxisOrder) {
    if (attribute3 === undefined) {
      labels = [...xAxisOrder, ...getAdditionalLabels(preprocessData, 0, xAxisOrder)];
    }else if (attribute2 != undefined && attribute3 === undefined) {
      datasetLabels = [...xAxisOrder, ...getAdditionalLabels(preprocessData, 1, xAxisOrder)];
    }else{
      labels = [...xAxisOrder, ...getAdditionalLabels(preprocessData, 0, xAxisOrder)];
    }
  }

  if (xAxisStackOrder) {
    datasetLabels = [...xAxisStackOrder, ...getAdditionalLabels(preprocessData, 1, xAxisStackOrder)];
  }

  if (yAxisOrder) {
    if (attribute3 === undefined) {
      datasetLabels = [...yAxisOrder, ...getAdditionalLabels(preprocessData, 1, yAxisOrder)];
    } else {
      stackLabels = [...yAxisOrder, ...getAdditionalLabels(preprocessData, 2, yAxisOrder)];
    }
  }

  if (xAxisLimit) {
    labels = labels.slice(0, xAxisLimit);
  }
    
  const datasets: ChartDataset<"bar", any[]>[] = datasetLabels.map(datasetLabel => {
    return stackLabels.length > 0
      ? stackLabels.map(stackLabel => ({
          label: `${datasetLabel} - ${stackLabel}`,
          data: labels.map(label => {
            return Object.entries(groupedData)
              .filter(([key]) => key.startsWith(`${label}|${datasetLabel}|${stackLabel}`))
              .reduce((acc, [key, value]) => acc + (value as number), 0);
          }),
          backgroundColor: context => {
            const label = context.chart.data.labels[context.dataIndex];
            return filteredValues.length === 0 || filteredValues.includes(label)
              ? 
              colors && colors[stackLabel] || 'rgba(75,192,192,0.4)'
              : 'rgba(169,169,169,0.4)';
          },
          stack: datasetLabel
        }))
      : {
          label: datasetLabel,
          data: labels.map(label => {
            return Object.entries(groupedData)
              .filter(([key]) => key.startsWith(`${label}${datasetLabel ? '|'+datasetLabel : ''}`))
              .reduce((acc, [key, value]) => acc + (value as number), 0);
          }),
          backgroundColor: context => {
            const label = context.chart.data.labels[context.dataIndex];
            return filteredValues.length === 0 || filteredValues.includes(label)
              ? colors && (colors[datasetLabel] || colors[label]) || 'rgba(75,192,192,0.4)'
              : 'rgba(169,169,169,0.4)';
          },
        };
  }).flat() as ChartDataset<"bar", any[]>[];

  const chartData = {
    labels: labels,
    datasets: datasets
  };

  const maxValue = attribute3 == undefined 
      ? 
      Math.max(...Object.values(groupedData).map(value => value as number))
      :
      Math.max(...labels.map(label => (
        Math.max(...datasetLabels.map(datasetLabel => (
          Object.entries(groupedData)
            .filter(([key]) => key.startsWith(`${label}|${datasetLabel}|`))
            .reduce((acc, [key, value]) => acc + (value as number), 0)
        ))))))

  const suggestedMax = maxValue + (maxValue * 0.2);

  const chartOptions = {
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            return labelFormatter ? labelFormatter(context[0]?.label) : context[0]?.label || '';
          },
          label: (context) => {
            if( attribute2 != undefined || attribute3 != undefined ){
              const datasetLabel = context.dataset.label || '';
              const dataValue = context.raw;
              return `${datasetLabel}: ${dataValue} ${label1}`;
            }else{
              return ''
            }
          },
          footer: (context) => {
            if( attribute2 != undefined || attribute3 != undefined ){
              const total = context.reduce((acc, item) => acc + item.raw, 0);
              return `Total: ${total}`;
            }else{
              return `${context[0].raw} ${label1}`;
            }
          }
        },
      },
      datalabels: {
        anchor: 'end' as const,
        align: 'top' as const,
        formatter: (value, context) => {
          if (attribute3 != undefined) {
            const datasets = context.chart.data.datasets;
            const total = datasets.filter((dataset) => dataset.stack == context.dataset.stack).reduce((acc, dataset) => {
              return acc + dataset.data[context.dataIndex];
            }, 0);
  
            const datasetIndex = context.datasetIndex;
            const lastDatasetIndex = datasets.length - 1;
  
            if ( datasetIndex === lastDatasetIndex || context.dataset.stack != datasets[datasetIndex+1].stack) {
              return total;
            } else {
              return '';
            }
          } else {
            return value;
          }
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: suggestedMax,
        stacked: attribute3 != undefined,
      },
      x: {
        ticks: {
          callback: (value, index, ticks) => {
            return labelFormatter ? labelFormatter(labels[index]) : labels[index];
          },
        },
        stacked: attribute3 != undefined,
      }
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  const chartRef = useRef<ChartJS<ChartType>>({} as ChartJS<ChartType>);

  const handleClick = (event) => {
    const chart = chartRef.current;
    if (chart) {
      const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
      if (elements.length > 0 && chart.data.labels) {
        const { index } = elements[0];
        const selectedValue = chart.data.labels[index] as string;
        if (filteredValues.includes(selectedValue)) {
          dispatch({ type: 'REMOVE_FILTER', payload: { attribute: attribute1, value: selectedValue } });
        } else {
          dispatch({ type: 'FILTER_BY_ATTRIBUTE', payload: { attribute: attribute1, value: selectedValue } });
        }
      }
    }
  };

  const clearFilter = () => dispatch({ type: 'REMOVE_FILTER', payload: { attribute: attribute1 } });

  return (
    <div className="card h-250px">
      <div className="card-header p-5 min-h-50px">
        <div className="card-title fw-semibold fs-6 d-flex flex-row">
          {title}
        {filteredValues.length > 0 && (
          <div className="d-flex align-items-center bg-light-primary ms-3 px-2 py-0 rounded border">
            <span className="fs-8">
              {`Filtered by: ${filteredValues.join(', ')}`}
            </span>
            <div className='btn btn-sm btn-icon btn-active-color-primary p-2 w-10px h-10px ms-1' onClick={clearFilter}>
              <i className='fa-solid fa-xmark fs-8'/>
            </div>
          </div>
        )}
          </div>
      </div>
      <div className="card-body p-5">
        
        <div className="d-flex position-relative">
          <Bar
            ref={chartRef}
            data={chartData}
            onClick={handleClick}
            options={chartOptions}
          />
        </div>
      </div>
    </div>
  );
};


const Messages : React.FC<Props> = ({}) => {

  const intl = useIntl()

  const [loaded, setLoaded] = useState<boolean>(false)
  const [hasNoData, setHasNoData] = useState<boolean>(false)

  const [refresh, setRefresh] = useState<string>()

  const [modelIDs, setModelIDs] = useState<any[]>([])
  const [modelColors, setModelColors] = useState<any>({})

  const [selectedLLMs, setSelectedLLMs] = useState<any[]>([])
  const [selectedUserTypes, setSelectedUserTypes] = useState<any[]>([])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [timezoneOffset, setTimezoneOffset] = useState<number>()

  const [promptTopics, setPromptTopics] = useState<string[]>([])

  const [state, dispatch] = useReducer(dataReducer, {
    data: [],
    filterCriteria: [], // Array of filter objects { attribute, value }
  });

  const refreshRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (refreshRef.current) {
      clearInterval(refreshRef.current);
    }

    if (refresh && !isNaN(parseInt(refresh))) {
      refreshRef.current = setInterval(() => {
        loadModelIDs()
        load()
      }, parseInt(refresh) * 1000)
    }

    return () => {
      if( refreshRef.current ){
        clearInterval(refreshRef.current)
        refreshRef.current = null
      }
    }
  }, [refresh])

  useEffect(() => {
    loadModelIDs()
  }, [startDate, endDate, timezoneOffset])

  useEffect(() => {
    load()
  }, [startDate, endDate, timezoneOffset, selectedLLMs, selectedUserTypes])

  useEffect(() => {
    if( modelIDs.length > 0){
      setModelColors(getRandomColors(modelIDs.map(modelId => formatModelId(modelId))))
    }
  }, [modelIDs])
  
  async function loadModelIDs () {
    if( startDate && endDate && timezoneOffset ){
      let {data: data} = await adminAPI.getChatMessageModelIDs(startDate, endDate, timezoneOffset)
      setModelIDs(data['data'])
    }
  }

  const handleDateSelection = (startDate: string, endDate:string, timezoneOffset:number) => {
    setStartDate(startDate)
    setEndDate(endDate)
    setTimezoneOffset(timezoneOffset)
  }

  const formatData = (data: any) => {
    return data.map((item: any) => {
      return {
        ...item,
        response_model_id: formatModelId(item.response_model_id),
        reaction: formatReaction(item.reaction)
      }
    })
  }

  async function load(){
    if( startDate && endDate && timezoneOffset ){
      setLoaded(false)
      let {data: data} = await adminAPI.getChatMessages(startDate, endDate, timezoneOffset, 'fanar', selectedLLMs, selectedUserTypes, true)
      if( data['data']['data'] ){
        setPromptTopics(data['data']['prompt_topics'])
        dispatch({ type: 'SET_DATA', payload: { data : formatData(data['data']['data'])}});
        setHasNoData(false)
      }else{
        setHasNoData(true)
      }
      setLoaded(true)
    }
  }

  return (
    <div className="row">
      <div className="col-12 mb-5 d-flex flex-row align-items-center justify-content-between">
        <div className="d-flex flex-row align-items-center">
          <BootstrapDateRangePicker onDateSelect={handleDateSelection} disabled={!loaded}/>
          <Select  
              className='react-select-styled ms-4 w-250px' 
              classNamePrefix='react-select' 
              options={modelIDs.map(modelId => ({'label':modelId, 'value':modelId}))} 
              placeholder='LLMs' 
              isMulti={true}
              isClearable={true}
              isSearchable={true}
              isDisabled={!loaded}
              onChange={(selectedOptions) => setSelectedLLMs(selectedOptions?.map(option => option.value))}
          />
          <Select  
              className='react-select-styled ms-4 w-250px' 
              classNamePrefix='react-select' 
              options={Object.values(UserType).filter(type => type != UserType.LOAD_TESTER).map(type => ({'label':toTitleCase(type), 'value':type}))} 
              placeholder='User Types' 
              isMulti={true}
              isClearable={true}
              isSearchable={true}
              isDisabled={!loaded}
              onChange={(selectedOptions) => setSelectedUserTypes(selectedOptions?.map(option => option.value))}
          />
          {!loaded && (
            <Spinner animation='border' className='ms-4'/>
          )}
        </div>
        <div className="d-flex flex-row align-items-center">
          <span>Refresh</span>
          <Select  
            className='react-select-styled ms-4 w-150px' 
            classNamePrefix='react-select' 
            options={[
              {'label':'Off', 'value':''}, 
              {'label':'Every 30 seconds', 'value':'30'}, 
              {'label':'Every 1 minute', 'value':'60'}, 
              {'label':'Every 5 minutes', 'value':'300'},
              {'label':'Every 10 minutes', 'value':'600'}
            ]} 
            placeholder='Off' 
            onChange={(selectedOption) => setRefresh(selectedOption?.value)}
          />
        </div>
      </div>
      {hasNoData 
      ?
        <div className="col-12 mb-5">
          <div className="alert alert-primary">No data</div>
        </div>
      :
      state.data && 
      <div className="col-12">
        <div className="row">
          <div className="col-3 mb-5">
            <DynamicChart 
              title="Reaction on Responses"
              data={state.data.filter(item => REACTION_CATEGORIES.includes(item.reaction))} 
              attribute1={'reaction'}
              label1={'responses'} 
              dispatch={dispatch}
              filterCriteria={state.filterCriteria}
              xAxisOrder={REACTION_CATEGORIES}
              colors={REACTION_COLORS}
            />
          </div>
          <div className="col-9 mb-5">
            <DynamicChart 
              title="Reaction on Responses across Models"
              data={state.data.filter(item => REACTION_CATEGORIES.includes(item.reaction))} 
              attribute1={'response_model_id'}
              attribute2={'reaction'} 
              label1={'responses'} 
              dispatch={dispatch}
              filterCriteria={state.filterCriteria}
              xAxisOrder={(selectedLLMs.length > 0 ? selectedLLMs : modelIDs).map(modelId => formatModelId(modelId))}
              xAxisStackOrder={REACTION_CATEGORIES}
              colors={REACTION_COLORS}
            />
          </div>
          <div className="col-12 mb-5">
            <DynamicChart 
              title="Reaction on Responses across Prompt Topics"
              data={state.data.filter(item => item.prompt_topic != undefined && item.prompt_topic != null && item.prompt_topic != '' && REACTION_CATEGORIES.includes(item.reaction))} 
              attribute1={'prompt_topic'}
              attribute2={'response_model_id'}
              attribute3={'reaction'}
              label1={'responses'} 
              dispatch={dispatch}
              filterCriteria={state.filterCriteria}
              xAxisOrder={promptTopics}
              xAxisStackOrder={(selectedLLMs.length > 0 ? selectedLLMs : modelIDs).map(modelId => formatModelId(modelId))}
              yAxisOrder={REACTION_CATEGORIES}
              colors={REACTION_COLORS}
            />
          </div>
          <div className="col-12 mb-5">
            <DynamicChart 
              title="Feedback on Bad Responses Generated by Different Models"
              data={state.data.filter(item => item.reaction == 'bad' && item.feedback_category != undefined && item.feedback_category != null && item.feedback_category != '' && FEEDBACK_CATEGORIES.includes(item.feedback_category)).flatMap(item => {
                  const categories = item.feedback_category.replace(', ', ',').split(',').map(category => category.trim());
                  
                  return categories.map(category => ({
                    ...item,
                    feedback_category: category
                  }));
                })
              } 
              attribute1={'feedback_category'}
              attribute2={'response_model_id'} 
              label1={'responses'} 
              dispatch={dispatch}
              filterCriteria={state.filterCriteria}
              xAxisOrder={FEEDBACK_CATEGORIES}
              xAxisStackOrder={(selectedLLMs.length > 0 ? selectedLLMs : modelIDs).map(modelId => formatModelId(modelId))}
              colors={modelColors}
              labelFormatter={(label) => intl.formatMessage({id: 'chat.message.feedback.categories.'+label})}
            />
          </div>
        </div>
      </div>
      }
    </div>
    )
}

export default Messages